import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppModalService } from '@datachain/ui-sdk/components/app-modal';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, combineLatest, filter, map, of, switchMap } from 'rxjs';

import { NgCommService } from '../../ajs-ng-communication.service';
import { AjsStateService } from '../../ajs-state.service';
import { SemanticRuleRequester } from '../../requesters/semantic-rule/semantic-rule.requester';
import {
	ISemanticRuleActionModalData,
	SemanticRuleAction,
	SemanticRuleActionPopupComponent,
} from '../components/semantic-rule-action-popup/semantic-rule-action-popup.component';
import { SemanticRuleAdapter } from '../semantic-rule.adapter';
import {
	deleteSemanticRule,
	deleteSemanticRuleFail,
	fetchSemanticRuleListFail,
	fetchSemanticRuleListSuccess,
	initSemanticRuleList,
	openDeleteConfirmationPopup,
	refreshSemanticRuleList,
} from './semantic-rule.actions';

@Injectable()
export class SemanticRuleEffects {
	// ##########################################################
	// Business Effects
	// ##########################################################

	public initSemanticRuleList$ = createEffect(() =>
		this.action$.pipe(
			ofType(initSemanticRuleList),
			switchMap(() => this.semanticRequester.getSemanticRuleList(true)),
			map((response) =>
				fetchSemanticRuleListSuccess({
					semanticRuleList:
						this.semanticRuleAdapter.generateSemanticRuleList(response),
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchSemanticRuleListFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public refreshSemanticRuleList$ = createEffect(() =>
		this.action$.pipe(
			ofType(refreshSemanticRuleList),
			switchMap(() => this.semanticRequester.getSemanticRuleList(false)),
			map((response) =>
				fetchSemanticRuleListSuccess({
					semanticRuleList:
						this.semanticRuleAdapter.generateSemanticRuleList(response),
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchSemanticRuleListFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public deleteSemanticRules$ = createEffect(() =>
		this.action$.pipe(
			ofType(deleteSemanticRule),
			switchMap((action) =>
				this.semanticRequester.deleteOne(action.semanticRuleId)
			),
			map(() => {
				this.ngCommService.notifyOnSuccess(
					$localize`:i18n=@@semantic-rule.delete.success:`
				);
				return refreshSemanticRuleList();
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.ngCommService.notifyOnError(
					$localize`:i18n=@@semantic-rule.delete.error:`
				);
				this.store.dispatch(
					deleteSemanticRuleFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	// ##########################################################
	// Confirmation popup effects
	// ##########################################################

	public openDeleteConfirmationPopup$ = createEffect(
		() =>
			this.action$.pipe(
				ofType(openDeleteConfirmationPopup),
				switchMap((action) => {
					const modalRef =
						this.modalService.openPopup<ISemanticRuleActionModalData>(
							SemanticRuleActionPopupComponent,
							{
								title: $localize`:i18n=@@semantic-rule.action.popup.delete.header:`,
								subTitle: '',
								semanticRuleId: action.semanticRuleId,
								element: action.semanticRuleLabel,
								action: SemanticRuleAction.Delete,
							},
							{
								disableClose: true,
							}
						);
					return combineLatest([of(action), modalRef.afterClosed()]);
				}),
				filter(([, modalResult]) => modalResult !== undefined),
				map(([action]) => {
					action.semanticRuleId.map((id) =>
						this.store.dispatch(
							deleteSemanticRule({
								semanticRuleId: id,
							})
						)
					);
				})
			),
		{
			dispatch: false,
		}
	);

	public constructor(
		private readonly action$: Actions,
		private readonly semanticRequester: SemanticRuleRequester,
		private readonly semanticRuleAdapter: SemanticRuleAdapter,
		private readonly stateService: AjsStateService,
		private readonly store: Store,
		private readonly modalService: AppModalService,
		private readonly ngCommService: NgCommService
	) {}
}
